import type { CSSProperties } from "react";

/**
 * Colors a 0–1 performance fraction on the shared severity ramp: green above
 * 0.8, then a widening red tint as it drops toward 0. Mirrors
 * `webui/app/src/pages/measurement/components/performanceTone.ts` and
 * `PerformanceValue.tsx` — duplicated here rather than imported because
 * widgets cannot depend on app/src. Both read the same `--sev-*` design
 * tokens, so the two stay visually identical without a shared import.
 */
export function getPerformanceValueStyle(fraction: number): CSSProperties | undefined {
  if (fraction >= 0.8) return undefined;
  if (fraction >= 0.7) return { color: "var(--sev-medium)" };
  if (fraction >= 0.6) return { color: "var(--sev-high)" };
  if (fraction >= 0.5) return { color: "var(--sev-critical)" };

  const backgroundMix =
    fraction < 0.1 ? 100 : fraction < 0.2 ? 80 : fraction < 0.3 ? 60 : fraction < 0.4 ? 40 : 20;

  return {
    color: "var(--sev-critical-strong)",
    backgroundColor: `color-mix(in oklch, var(--sev-critical-bg), var(--sev-critical-strong-bg) ${backgroundMix}%)`,
  };
}

const percentFormat = new Intl.NumberFormat("en", { style: "percent", maximumFractionDigits: 0 });

/** A percentage value tinted by how far it sits from a healthy 80%+. */
export function PerformanceValue({ fraction }: { fraction: number }) {
  const attentionLabel =
    fraction < 0.5
      ? "critical attention level"
      : fraction < 0.6
        ? "high attention level"
        : fraction < 0.8
          ? "medium attention level"
          : undefined;

  return (
    <span
      className="inline-flex min-w-11 justify-center rounded-sm px-1.5 py-0.5 font-semibold tabular-nums"
      style={getPerformanceValueStyle(fraction)}
      aria-label={attentionLabel ? `${percentFormat.format(fraction)}, ${attentionLabel}` : undefined}
      title={attentionLabel}
    >
      {percentFormat.format(fraction)}
    </span>
  );
}
